import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  AdminBusinessListPage,
  AdminExtendSubscriptionResult,
} from "../../interfaces/AdminBusinessListObject";
import { getRequest, postRequest } from "../http/httpRequest";
import {
  ApiEnvelopeError,
  coerceSingle,
  unwrapEnvelope,
} from "../helpers/ApiEnvelopeHelper";
import {
  convertAdminBusinessListPage,
  convertAdminExtendSubscriptionResult,
} from "../converters/backendToUi/adminBusinessListConvertor";

// Admin -> Businesses -> All Businesses. GET, bearer, same third envelope
// shape AdminSubscriptionApiService already tolerates via ApiEnvelopeHelper.
const BEARER = true;

const FRIENDLY_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: "Your session has expired. Please log in again.",
  UNAUTHORIZED: "You don't have access to the businesses list.",
  ACCESS_DENIED: "You don't have access to the businesses list.",
  FORBIDDEN: "You don't have access to the businesses list.",
  HTTP_403: "You don't have access to the businesses list.",
};

export function toFriendlyAdminBusinessListError(error: unknown): string {
  if (error instanceof ApiEnvelopeError) {
    return FRIENDLY_MESSAGES[error.errorCode] ?? error.message;
  }
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

export interface ListBusinessesInput {
  expired: boolean;
  page: number;
  size: number;
}

/**
 * One page of every business on the platform.
 *
 * `expired` is sent as a query param ONLY when true - omitting it is what the
 * backend treats as "non-expired" (confirmed against a live call rather than
 * assumed; there's no separate `expired=false` variant to fall back on).
 */
async function listBusinesses({
  expired,
  page,
  size,
}: ListBusinessesInput): Promise<AdminBusinessListPage> {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (expired) params.set("expired", "true");
  const endpoint = `${ServiceEndpoints.BUSINESS_LIST}?${params.toString()}`;

  const response = await getRequest(endpoint, BEARER);
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertAdminBusinessListPage(contents);
}

export interface ExtendSubscriptionInput {
  businessId: string;
  days: number;
}

const EXTEND_SUBSCRIPTION_FRIENDLY_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: "Your session has expired. Please log in again.",
  UNAUTHORIZED: "You don't have access to extend subscriptions.",
  ACCESS_DENIED: "You don't have access to extend subscriptions.",
  FORBIDDEN: "You don't have access to extend subscriptions.",
  HTTP_403: "You don't have access to extend subscriptions.",
};

export function toFriendlyExtendSubscriptionError(error: unknown): string {
  if (error instanceof ApiEnvelopeError) {
    return EXTEND_SUBSCRIPTION_FRIENDLY_MESSAGES[error.errorCode] ?? error.message;
  }
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

/**
 * Manually push a business's subscription end date out by `days`. POST, no
 * body - `days` is a query param, matching the sample call Sai supplied
 * (`?days=2`). Same array-wrapped-single-object contents as BUSINESS_DECISION,
 * so this reuses coerceSingle the same way listBusinesses does for the page
 * object.
 */
async function extendSubscription({
  businessId,
  days,
}: ExtendSubscriptionInput): Promise<AdminExtendSubscriptionResult> {
  const params = new URLSearchParams({ days: String(days) });
  const endpoint = `${ServiceEndpoints.BUSINESS_EXTEND_SUBSCRIPTION_BASE}/${businessId}/extend-subscription?${params.toString()}`;
  // No request body needed - `{}` rather than `null`, matching every other
  // bodiless POST in the repo (postRequest JSON.stringifies the payload, and
  // a bare `null` body has burned other backends before).
  const response = await postRequest(endpoint, {}, BEARER);
  const contents = coerceSingle(await unwrapEnvelope<unknown>(response));
  return convertAdminExtendSubscriptionResult(contents);
}

const AdminBusinessListApiService = {
  listBusinesses,
  extendSubscription,
};

export default AdminBusinessListApiService;
